import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import Seo from "@/lib/Seo";
import PageHero from "@/components/common/PageHero";
import { easeOut } from "@/components/common/Reveal";
import { BLOG_POSTS } from "@/data/blog";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });

export default function Blog() {
  return (
    <>
      <Seo
        title="Blog & actualités"
        description="Guides, conseils et actualités sur les formations transport, permis poids lourd, TP ECSR, FIMO/FCO. Financement CPF, débouchés et reconversion : les experts EDUCASER GROUP répondent."
        path="/blog"
        schema={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Blog EDUCASER GROUP",
          blogPost: BLOG_POSTS.map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            datePublished: p.publishedAt,
            author: { "@type": "Organization", name: "EDUCASER GROUP" },
          })),
        }}
      />
      <PageHero
        breadcrumb={[{ label: "Accueil", to: "/" }, { label: "Blog" }]}
        eyebrow="Actualités & conseils"
        title="Actualités & Blog EDUCASER GROUP"
        intro="Actualités du secteur (bientôt via flux officiels), guides pratiques et conseils d'experts sur les formations transport et les métiers de la conduite."
      />

      <div className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {BLOG_POSTS.map((post, i) => (
              <motion.article
                key={post.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, ease: easeOut, delay: (i % 3) * 0.08 }}
                className="group flex flex-col overflow-hidden rounded-2xl bg-white shadow-[0_10px_30px_rgba(0,35,102,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(0,35,102,0.12)]"
                data-testid={`blog-card-${post.slug}`}
              >
                <Link to={`/blog/${post.slug}`} className="block">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={post.cover} alt={post.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy" />
                    <span className="absolute left-4 top-4 rounded-full bg-brand-red px-3 py-1 text-xs font-semibold text-white">
                      {post.category}
                    </span>
                  </div>
                </Link>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {fmtDate(post.publishedAt)}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {post.readingTime} min</span>
                  </div>
                  <h2 className="mt-3 font-display text-xl font-bold text-brand-blue transition-colors group-hover:text-brand-red">
                    <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                  </h2>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-500">{post.excerpt}</p>
                  <Link to={`/blog/${post.slug}`}
                    className="mt-5 inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-brand-red transition-colors hover:text-brand-red-light"
                    data-testid={`blog-read-${post.slug}`}>
                    Lire l'article <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
